/**
 * 存储抽象：持久化层的**唯一**后端契约。
 *
 * 存在的第一个理由是**可测**：jsdom 里没有 IndexedDB，如果上层直接调 `indexedDB.open`，
 * 那么持久化的全部逻辑在测试里都跑不起来，只能靠手点浏览器验证。把后端收窄成这几个方法后，
 * 测试可以用内存实现覆盖全部业务逻辑，用 `fake-indexeddb` 驱动真实的 IndexedDB 实现，
 * 两者跑的是同一套断言。
 *
 * 第二个理由是**降级**：隐私模式/禁用站点数据时 IndexedDB 根本打不开，此时换成内存实现，
 * 上层一行都不用改（只需要读 `kind` 告诉用户"这次不会保存"）。
 *
 * 关于接口该有多窄，取舍是这样的：
 *
 * - **不做 ORM 式查询**。需要"按条件查"的只有审计日志与飞前检查记录，而它们的条件都能落到
 *   键的前缀与顺序上（键里带了单调时间戳），因此 `keys()` + 前缀就够了。引入索引存储会让两个
 *   实现都要维护一套索引一致性，而收益只是省几十次 `get`。
 * - **必须有 `batch()`**。它是"事务"在这里的代名词，但语义更窄也更好实现：要么全部生效，
 *   要么全部不生效。参数快照（头部 + 分片）与航线草稿（正文 + "上次编辑的是谁"指针）
 *   都必须原子写入 —— 半份快照会让用户以为"这就是上次那份参数"，而指针指向不存在的草稿
 *   会让崩溃恢复直接落空。
 * - **不做游标/分页**。数据量上限由我们自己的轮转策略兜住（审计 2000 条、草稿 5 份），
 *   不需要流式读取。
 */

/** 存储里的集合。每个集合对应 IndexedDB 里的一个 object store。 */
export type PersistenceCollection =
  | 'settings'
  | 'param-snapshots'
  | 'mission-drafts'
  | 'preflight-records'
  | 'audit-log'
  | 'meta';

export const PERSISTENCE_COLLECTIONS: readonly PersistenceCollection[] = [
  'settings',
  'param-snapshots',
  'mission-drafts',
  'preflight-records',
  'audit-log',
  'meta',
];

/** 写入一条记录。`key` 由调用方给出：键的构造是各仓库的职责（见各自的键约定注释）。 */
export interface StorePut {
  readonly kind: 'put';
  readonly collection: PersistenceCollection;
  readonly key: string;
  readonly value: unknown;
}

export interface StoreDelete {
  readonly kind: 'delete';
  readonly collection: PersistenceCollection;
  readonly key: string;
}

export type StoreOperation = StorePut | StoreDelete;

/** 后端种类。`memory` 意味着"本次会话的改动不会被保存"，界面必须据此提示用户。 */
export type StoreKind = 'indexeddb' | 'memory';

export interface KeyValueStore {
  readonly kind: StoreKind;
  /** 读一条记录；不存在返回 `undefined` */
  get(collection: PersistenceCollection, key: string): Promise<unknown | undefined>;
  put(collection: PersistenceCollection, key: string, value: unknown): Promise<void>;
  remove(collection: PersistenceCollection, key: string): Promise<void>;
  /**
   * 列出集合里的键，按**字典序**（不是插入序）。
   * IndexedDB 的 `getAllKeys` 就是字典序，内存实现必须与它一致，否则"最近 N 条"这类
   * 依赖键顺序的逻辑会在两种后端下给出不同结果。
   */
  keys(collection: PersistenceCollection, prefix?: string): Promise<readonly string[]>;
  /** 原子批量写：任一条失败则整批不生效 */
  batch(operations: readonly StoreOperation[]): Promise<void>;
  /** 释放连接。关闭后再调用其它方法必须报错，而不是静默失败。 */
  close(): Promise<void>;
}

/** 键是否以 `prefix` 开头；`prefix` 为空表示全部 */
export function matchesPrefix(key: string, prefix: string | undefined): boolean {
  return prefix === undefined || prefix.length === 0 || key.startsWith(prefix);
}

/** 过滤 + 排序：所有实现都必须给出同一顺序，所以排序放在这唯一的共用函数里 */
export function selectKeys(keys: readonly string[], prefix: string | undefined): string[] {
  const selected = keys.filter((key) => matchesPrefix(key, prefix));
  // `Array.prototype.sort` 默认按 UTF-16 码元比较，与 IndexedDB 的键序（V8 里对字符串就是码元序）一致
  selected.sort();
  return selected;
}
